/**
 * @fileoverview Utility functions for document screenshot handling and display.
 *
 * This module provides helper functions for processing document upload screenshots,
 * determining visual styling based on upload status, and sorting images by priority.
 *
 * @module documentScreenshotUtils
 */

import { ImageViewerWithTextAndBadgeProps } from "../components/imageViewerWithTextAndBadge/imageViewerWithTextAndBadge";
import { NOT_INITIATED } from "../constants/stringLiterals";
import {
  AuthOnFile,
  DocumentUploadScreenshot,
  JCodeDetails,
  MedicalPaOrder,
} from "../data-model/medicalPaOrdersModel";
import { DocWriteBackStatus } from "../enums/evBvWriteBackStatus";

/**
 * Priority levels for document upload status sorting.
 *
 * @description
 * Used to determine the display order of documents based on their upload status.
 * Higher values indicate higher priority and will appear first when sorting.
 *
 * @enum {number}
 */
export enum DocumentPriority {
  /** Default priority for unknown or unhandled statuses */
  DEFAULT = 1,
  /** Priority for successfully uploaded documents */
  SUCCESS = 2,
  /** Priority for documents currently being uploaded */
  IN_PROGRESS = 3,
  /** Highest priority for documents with upload errors */
  ERROR = 4,
}

/**
 * Sorts an array of images based on their document status priority in descending order.
 *
 * @description
 * Images with higher priority values appear first. This is useful for displaying
 * error states prominently, followed by in-progress, success, and other statuses.
 *
 * @param {ImageViewerWithTextAndBadgeProps[]} imagesWithBadges - Array of image objects with priority field
 * @returns {ImageViewerWithTextAndBadgeProps[]} Sorted array with highest priority first
 *
 * @example
 * const images = [
 *   { imagePath: '/path1', priroity: 2 },
 *   { imagePath: '/path2', priroity: 4 },
 *   { imagePath: '/path3', priroity: 1 }
 * ];
 * const sorted = sortImageBasedOnDocStatusInDescendingOrder(images);
 * // Returns: [{ priroity: 4 }, { priroity: 2 }, { priroity: 1 }]
 */
export const sortImageBasedOnDocStatusInDescendingOrder = (
  imagesWithBadges: ImageViewerWithTextAndBadgeProps[],
): ImageViewerWithTextAndBadgeProps[] => {
  return [...imagesWithBadges].sort(
    (a, b) => (b.priroity ?? 0) - (a.priroity ?? 0),
  );
};

/**
 * Retrieves the screenshot path for a given document path.
 *
 * @description
 * Searches through the document upload screenshots array to find the matching
 * screenshot based on the document path.
 *
 * @param {DocumentUploadScreenshot[]} screenshots - Array of document upload screenshots
 * @param {string} docPath - The document path to search for
 * @returns {string} The screenshot path if found, empty string otherwise
 *
 * @example
 * const screenshots = [
 *   { document_path: '/docs/file1.pdf', screenshot_path: '/screenshots/ss1.png' }
 * ];
 * const path = getScreenshotPathByDocPath(screenshots, '/docs/file1.pdf');
 * // Returns: '/screenshots/ss1.png'
 */
export const getScreenshotPathByDocPath = (
  screenshots: DocumentUploadScreenshot[] | undefined,
  docPath: string,
): string => {
  return (
    screenshots?.find((screenshot) => screenshot.document_path === docPath)
      ?.screenshot_path ?? ""
  );
};

/**
 * Retrieves the creation timestamp for a screenshot by its document path.
 *
 * @description
 * Searches through the document upload screenshots array to find the matching
 * screenshot's creation date based on the document path.
 *
 * @param {DocumentUploadScreenshot[]} screenshots - Array of document upload screenshots
 * @param {string} docPath - The document path to search for
 * @returns {string} The created_at timestamp if found, empty string otherwise
 *
 * @example
 * const screenshots = [
 *   { document_path: '/docs/file1.pdf', created_at: '2024-01-15T10:30:00Z' }
 * ];
 * const timestamp = getUpdatedAtByDocPath(screenshots, '/docs/file1.pdf');
 * // Returns: '2024-01-15T10:30:00Z'
 */
export const getUpdatedAtByDocPath = (
  screenshots: DocumentUploadScreenshot[] | undefined,
  docPath: string,
): string => {
  return (
    screenshots?.find((screenshot) => screenshot.document_path === docPath)
      ?.created_at ?? ""
  );
};

/**
 * Returns the background color for a badge based on the document upload status.
 *
 * @description
 * Maps upload status values to their corresponding background colors:
 * - SUCCESS: Light green (#E6F3F0)
 * - ERROR: Light red (#FFE8E8)
 * - IN_PROGRESS: Light yellow (#FFFCD6)
 * - Default: Light gray (#F5F5F5)
 *
 * @param {string} uploadStatus - The document upload status
 * @returns {string} Hex color code for the background
 *
 * @example
 * getBatchBgColorByUploadStatus('success'); // Returns: '#E6F3F0'
 * getBatchBgColorByUploadStatus('error');   // Returns: '#FFE8E8'
 */
export const getBatchBgColorByUploadStatus = (uploadStatus: string): string => {
  return uploadStatus === DocWriteBackStatus.SUCCESS
    ? "#E6F3F0"
    : uploadStatus === DocWriteBackStatus.ERROR
      ? "#FFE8E8"
      : uploadStatus === DocWriteBackStatus.IN_PROGRESS
        ? "#FFFCD6"
        : "#F5F5F5";
};

/**
 * Returns the text color for a badge based on the document upload status.
 *
 * @description
 * Maps upload status values to their corresponding text colors:
 * - SUCCESS: Dark green (#005D49)
 * - ERROR: Dark red (#CC0300)
 * - IN_PROGRESS: Dark yellow (#665D00)
 * - Default: Near black (#0F0F0F)
 *
 * @param {string} uploadStatus - The document upload status
 * @returns {string} Hex color code for the text
 *
 * @example
 * getBatchTextColorByUploadStatus('success'); // Returns: '#005D49'
 * getBatchTextColorByUploadStatus('error');   // Returns: '#CC0300'
 */
export const getBatchTextColorByUploadStatus = (
  uploadStatus: string,
): string => {
  return uploadStatus === DocWriteBackStatus.SUCCESS
    ? "#005D49"
    : uploadStatus === DocWriteBackStatus.ERROR
      ? "#CC0300"
      : uploadStatus === DocWriteBackStatus.IN_PROGRESS
        ? "#665D00"
        : "#0F0F0F";
};

/**
 * Returns the sorting priority for a document based on its upload status.
 *
 * @description
 * Maps document upload statuses to {@link DocumentPriority} values for sorting purposes.
 * Higher priority values indicate more important statuses that should appear first:
 *
 * | Status        | Priority                        | Value |
 * |---------------|--------------------------------|-------|
 * | ERROR         | {@link DocumentPriority.ERROR} | 4     |
 * | IN_PROGRESS   | {@link DocumentPriority.IN_PROGRESS} | 3 |
 * | SUCCESS       | {@link DocumentPriority.SUCCESS} | 2   |
 * | Other/Unknown | {@link DocumentPriority.DEFAULT} | 1   |
 *
 * @param {string} uploadStatus - The document upload status from {@link DocWriteBackStatus}
 * @returns {DocumentPriority} Priority value from the DocumentPriority enum
 *
 * @see {@link DocumentPriority} for the enum definition
 * @see {@link sortImageBasedOnDocStatusInDescendingOrder} for usage in sorting
 *
 * @example
 * getPriorityByUploadStatus(DocWriteBackStatus.ERROR);       // Returns: DocumentPriority.ERROR (4)
 * getPriorityByUploadStatus(DocWriteBackStatus.IN_PROGRESS); // Returns: DocumentPriority.IN_PROGRESS (3)
 * getPriorityByUploadStatus(DocWriteBackStatus.SUCCESS);     // Returns: DocumentPriority.SUCCESS (2)
 * getPriorityByUploadStatus('unknown');                      // Returns: DocumentPriority.DEFAULT (1)
 */
export const getPriorityByUploadStatus = (
  uploadStatus: string,
): DocumentPriority => {
  return uploadStatus === DocWriteBackStatus.ERROR
    ? DocumentPriority.ERROR
    : uploadStatus === DocWriteBackStatus.IN_PROGRESS
      ? DocumentPriority.IN_PROGRESS
      : uploadStatus === DocWriteBackStatus.SUCCESS
        ? DocumentPriority.SUCCESS
        : DocumentPriority.DEFAULT;
};

/**
 * Generates image viewer data for a single J-code's documents.
 *
 * @description
 * Transforms the documents array from a J-code into an array of
 * ImageViewerWithTextAndBadgeProps, applying status-based styling
 * and extracting screenshot paths.
 *
 * @param {JCodeDetails} jCode - The J-code containing documents
 * @param {DocumentUploadScreenshot[]} screenshots - Array of document upload screenshots
 * @returns {ImageViewerWithTextAndBadgeProps[]} Array of image viewer props for the J-code's documents
 *
 * @example
 * const jCode = { documents: [{ doc_path: '/path', upload_status: 'success' }] };
 * const screenshots = [{ document_path: '/path', screenshot_path: '/ss.png' }];
 * const images = generateImageDataForJCode(jCode, screenshots);
 */
export const generateImageDataForJCode = (
  jCode: JCodeDetails,
  screenshots: DocumentUploadScreenshot[] | undefined,
): ImageViewerWithTextAndBadgeProps[] => {
  const documents = jCode?.documents ?? [];

  return documents.map((doc, index) => ({
    imagePath: getScreenshotPathByDocPath(screenshots, doc?.doc_path ?? ""),
    badgeDetails: {
      batchText: doc?.upload_status ?? NOT_INITIATED,
      batchBgColor: getBatchBgColorByUploadStatus(
        doc?.upload_status ?? DocWriteBackStatus.NOT_INITIATED,
      ),
      batchTextColor: getBatchTextColorByUploadStatus(
        doc?.upload_status ?? DocWriteBackStatus.NOT_INITIATED,
      ),
      batchId: doc?.id ?? "",
    },
    shouldShowBottomBorder: index !== documents.length - 1,
    text: doc?.doc_title ?? "No Title",
    updatedAt: getUpdatedAtByDocPath(screenshots, doc?.doc_path ?? ""),
    priroity: getPriorityByUploadStatus(
      doc?.upload_status ?? DocWriteBackStatus.NOT_INITIATED,
    ),
  }));
};

/**
 * Generates sorted image viewer data for all J-codes in an auth-on-file record.
 *
 * @description
 * Processes an {@link AuthOnFile} object (fetched from Postgres) and extracts
 * document screenshot data from all J-codes across all auth entries. It:
 * 1. Iterates through all entries in the record
 * 2. For each entry, processes all j_codes
 * 3. For each J-code, generates image viewer data for all documents
 * 4. Combines all images and sorts them by priority (errors first)
 *
 * The returned images are sorted in the following priority order:
 * - ERROR: Shown first (priority 4)
 * - IN_PROGRESS: Shown second (priority 3)
 * - SUCCESS: Shown third (priority 2)
 * - Other/Unknown: Shown last (priority 1)
 *
 * @param authOnFile - The auth-on-file record containing entries and screenshots
 * @returns Sorted array of image viewer props for all documents
 */
export const generateImageDataForOrder = (
  authOnFile: AuthOnFile | undefined,
): ImageViewerWithTextAndBadgeProps[] => {
  if (!authOnFile) {
    return [];
  }

  const authEntries = authOnFile?.entries ?? [];
  const screenshots = authOnFile?.screenshots?.document_upload_screenshots;

  const allImages: ImageViewerWithTextAndBadgeProps[] = [];

  // Iterate through all auth entries
  authEntries.forEach((authEntry) => {
    const jCodes = authEntry?.j_codes ?? [];

    // Iterate through all J-codes in each auth entry
    jCodes.forEach((jCode) => {
      const jCodeImages = generateImageDataForJCode(jCode, screenshots);
      allImages.push(...jCodeImages);
    });
  });

  // Sort all images by priority (errors first)
  return sortImageBasedOnDocStatusInDescendingOrder(allImages);
};

/**
 * Result type for grouped image data by J-code.
 */
export interface JCodeImageData {
  /** The J-code identifier */
  jCodeId: string;
  /** The J-code value (e.g., "J9999") */
  jCode: string;
  /** Description or medicine name for the J-code */
  description: string;
  /** Sorted array of image viewer props for this J-code's documents */
  images: ImageViewerWithTextAndBadgeProps[];
}

/**
 * Generates image viewer data grouped by J-code for a medical PA order.
 *
 * @description
 * Similar to `generateImageDataForOrder`, but returns data grouped by J-code
 * instead of a flat array. This is useful when you want to display documents
 * organized by their parent J-code.
 *
 * Each J-code group contains:
 * - jCodeId: Unique identifier for the J-code
 * - jCode: The J-code value (e.g., "J9999")
 * - description: Medicine name or description
 * - images: Sorted array of document screenshots
 *
 * @param {MedicalPaOrder} orderData - The medical PA order
 * @returns {JCodeImageData[]} Array of J-code groups with their document images
 *
 * @example
 * const orderData = { ... };
 * const groupedImages = generateImageDataGroupedByJCode(orderData);
 * // Returns: [
 * //   { jCodeId: '123', jCode: 'J9999', description: 'Drug A', images: [...] },
 * //   { jCodeId: '456', jCode: 'J8888', description: 'Drug B', images: [...] }
 * // ]
 */
export const generateImageDataGroupedByJCode = (
  orderData: MedicalPaOrder | undefined,
): JCodeImageData[] => {
  if (!orderData) {
    return [];
  }

  const authEntries = orderData?.auth_on_file?.entries ?? [];
  const screenshots =
    orderData?.auth_on_file?.screenshots?.document_upload_screenshots;

  const groupedData: JCodeImageData[] = [];

  // Iterate through all auth entries
  authEntries.forEach((authEntry) => {
    const jCodes = authEntry?.j_codes ?? [];

    // Iterate through all J-codes in each auth entry
    jCodes.forEach((jCode) => {
      const jCodeImages = generateImageDataForJCode(jCode, screenshots);
      const sortedImages =
        sortImageBasedOnDocStatusInDescendingOrder(jCodeImages);

      groupedData.push({
        jCodeId: jCode?.id ?? "",
        jCode: jCode?.j_code ?? "",
        description: jCode?.medicine_name || jCode?.description || "N/A",
        images: sortedImages,
      });
    });
  });

  return groupedData;
};
